import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { track } from "@/lib/analytics";
import {
  getOnboardingStatus,
  getProfile,
  putProfile,
  extractProfileFieldErrors,
  type LangCode,
  type ProfileFieldError,
  type UserProfileUpdate,
} from "@/lib/profile-api";
import { ApiError } from "@/lib/api";
import { retryWithBackoff } from "@/lib/retry";
import type { UsStateCode } from "@/lib/states";
import { getCountry } from "@/lib/countries";
import {
  formatLanguageLabel,
  getLanguage,
  langCodeToI18nLocale,
} from "@/lib/languages";
import {
  readOnboardingPrefillFromWindow,
  type OnboardingPrefill,
} from "@/lib/onboarding-prefill";

import StepIndicator from "@/components/onboarding/StepIndicator";
import HomeCountryStep from "@/components/onboarding/HomeCountryStep";
import NativeLanguageStep from "@/components/onboarding/NativeLanguageStep";
import TargetStatesStep from "@/components/onboarding/TargetStatesStep";
import CurrentFocusStateStep from "@/components/onboarding/CurrentFocusStateStep";
import CdlInterestStep from "@/components/onboarding/CdlInterestStep";

/**
 * apps/web :: components/onboarding :: OnboardingWizard
 *
 * Multi-step container for the first-login wizard. Owns:
 *   - The in-progress `UserProfileUpdate` draft. Steps patch-merge their
 *     outputs back in; only the final Review → Submit button calls the
 *     server.
 *   - The current step index (0 = home_country ... 5 = review).
 *   - Mount-time `getOnboardingStatus()` + optional `getProfile()` pre-fill
 *     so partially-completed users resume where they left off.
 *
 * Navigation contract: on successful submit we navigate to the location
 * preserved in router state (`state.returnTo`) or `/` as the default.
 */
type StepKind =
  | "home_country"
  | "native_language"
  | "target_states"
  | "current_focus_state"
  | "cdl_interest"
  | "review";

// ROADS-508: native_language is deliberately FIRST.
// User-test feedback: immigrants arriving on the signup page in English
// don't realise the UI is translatable until they've already parsed
// several English screens. Putting language selection first lets the
// rest of onboarding render in their language. handleNativeLanguageNext
// calls i18n.changeLanguage() so the very next step ships translated.
const STEP_ORDER: readonly StepKind[] = [
  "native_language",
  "home_country",
  "target_states",
  "current_focus_state",
  "cdl_interest",
  "review",
];

/** Map wire-format nextStep → our local StepKind used to pick an index. */
function nextStepToKind(
  nextStep: string | undefined | null,
): StepKind {
  switch (nextStep) {
    case "native_language":
      return "native_language";
    case "target_states":
      return "target_states";
    case "current_focus_state":
      return "current_focus_state";
    case "done":
      // Server says done but user is on /onboarding — they can still edit.
      // Drop them on review so they can confirm + navigate away.
      return "review";
    default:
      return "home_country";
  }
}

interface DraftProfile {
  home_country: string | null;
  native_language: LangCode | null;
  target_states: UsStateCode[];
  current_focus_state: UsStateCode | null;
  cdl_interest: boolean;
}

const EMPTY_DRAFT: DraftProfile = {
  home_country: null,
  native_language: null,
  target_states: [],
  current_focus_state: null,
  cdl_interest: false,
};

/**
 * Load-failure source. Used to choose the right i18n message — a 5xx after
 * retries means the server's awake-but-unhappy ("server"), while
 * everything else (TypeError from fetch, DNS failure, offline) means the
 * request never reached us ("network"). 4xx errors land in `server` too —
 * they're rare here (the only ones we'd see are 401, which ProtectedRoute
 * already redirects on, and 404, which we treat as data not error).
 */
type LoadErrorVariant = "server" | "network";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "error"; message: string; variant: LoadErrorVariant };

type SubmitState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "error"; message: string; fieldErrors: ProfileFieldError[] };

export default function OnboardingWizard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [draft, setDraft] = useState<DraftProfile>(EMPTY_DRAFT);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [submit, setSubmit] = useState<SubmitState>({ kind: "idle" });

  // Mount: fetch status + profile, fast-forward to resume step.
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      try {
        // Wave 14e (ROADS-339, AC #1): the onboarding-status probe runs at
        // mount and a single 502 mid-deploy used to throw the wizard
        // straight into the load-error state. Wrap it in our backoff
        // helper — 5xx + network errors get up to 3 attempts (1s, 2s);
        // 4xx errors (404 means "no profile", 401 means session lapsed)
        // surface immediately so we don't hide them from the caller.
        const status = await retryWithBackoff(() => getOnboardingStatus());
        // Hydrate from stored profile when available (needed for the
        // resume-from-partial test — the wizard should render with
        // target_states already populated).
        let hydrated: DraftProfile = EMPTY_DRAFT;
        if (status.hasProfile) {
          const profile = await getProfile();
          if (profile) {
            hydrated = {
              home_country: profile.home_country || null,
              native_language: profile.native_language ?? null,
              target_states: profile.target_states ?? [],
              current_focus_state: profile.current_focus_state ?? null,
              cdl_interest: profile.cdl_interest ?? false,
            };
          }
        }
        if (cancelled) return;

        // ROADS-433: marketing funnel-quiz hand-off. Read the
        // `?state=&country=&hasLicense=&adult=` query params and merge
        // them into the draft. Stored profile fields win — a returning
        // user shouldn't lose their server-side answers because they
        // bounced through the marketing funnel a second time.
        const prefill = readOnboardingPrefillFromWindow();
        const merged = applyOnboardingPrefill(hydrated, prefill);
        setDraft(merged);

        // If the server says where to start, honour that. Otherwise fast-
        // forward past any steps the prefill answered. The spec is
        // backwards-compatible: omitted prefill fields fall through to
        // the server's nextStep.
        const startKind = nextStepToKind(status.nextStep);
        const baseIdx = STEP_ORDER.indexOf(startKind);
        const skipIdx = computePrefillSkipIndex(merged, prefill);
        let idx = Math.max(baseIdx < 0 ? 0 : baseIdx, skipIdx);
        // ROADS-508: server's onboarding-status doesn't yet know about the
        // wizard's reordered STEP_ORDER (native_language first). If the
        // user hasn't picked a language yet, override and start at the
        // language step regardless of what the server picked as
        // first-missing — they need the locale set before they parse any
        // other step. Already-picked-language users keep server-driven
        // resume behaviour.
        if (merged.native_language === null) {
          idx = STEP_ORDER.indexOf("native_language");
        }
        setStepIndex(idx);
        setLoad({ kind: "ready" });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : String(err);
        // Wave 14e/4 (ROADS-339): split server vs network so we can show a
        // friendlier i18n message ("our server hiccupped" vs "check your
        // connection") instead of a single "loadError" string. ApiError
        // means the request reached the server and came back non-2xx;
        // anything else means we never got a response.
        const variant: LoadErrorVariant =
          err instanceof ApiError ? "server" : "network";
        setLoad({ kind: "error", message, variant });
      }
    }
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const stepForward = useCallback(() => {
    setStepIndex((i) => Math.min(STEP_ORDER.length - 1, i + 1));
  }, []);

  // ROADS-505: when target_states has exactly one entry, the focus-state
  // step is auto-skipped on the forward pass (handled in
  // handleTargetStatesNext below). Back must mirror that — without this
  // mirror, pressing Back from cdl_interest lands the user on the
  // focus-state step, which used to auto-fire its useEffect and bounce
  // back to cdl_interest. From the user's POV: "Back does nothing."
  const stepBack = useCallback(() => {
    setStepIndex((i) => {
      const candidate = Math.max(0, i - 1);
      if (
        STEP_ORDER[candidate] === "current_focus_state" &&
        draft.target_states.length === 1
      ) {
        return Math.max(0, candidate - 1);
      }
      return candidate;
    });
  }, [draft.target_states.length]);

  const handleHomeCountryNext = useCallback(
    (code: string) => {
      setDraft((d) => ({ ...d, home_country: code }));
      // Analytics: `home_country` VALUE must NEVER be forwarded (§7 hard
      // rule). Only the step identifier goes to PostHog — the raw ISO
      // code stays in server-side profile storage.
      track("onboarding.step_completed", { step: "home_country" });
      stepForward();
    },
    [stepForward],
  );

  const handleNativeLanguageNext = useCallback(
    (code: LangCode) => {
      setDraft((d) => ({ ...d, native_language: code }));
      // ROADS-508: language is now step 1; immediately re-render the rest
      // of the wizard in the chosen locale so the country list + every
      // subsequent step ships translated. Codes without a UI bundle yet
      // (te/ru/uk per ROADS-508 out-of-scope) keep the previous i18n
      // locale rather than triggering a missing-key fallback storm.
      const target = langCodeToI18nLocale(code);
      if (target !== null && target !== i18n.language) {
        void i18n.changeLanguage(target);
      }
      track("onboarding.step_completed", { step: "native_language" });
      stepForward();
    },
    [stepForward, i18n],
  );

  const handleTargetStatesNext = useCallback(
    (states: UsStateCode[]) => {
      setDraft((d) => {
        // If the currently-selected focus state is no longer in target_states,
        // clear it so the next step starts clean. When exactly 1 state is
        // selected, the focus-state step is auto-skipped (see below) — pin
        // the focus to that sole state so the draft stays consistent.
        const nextFocus =
          states.length === 1
            ? (states[0] ?? null)
            : d.current_focus_state !== null &&
                states.includes(d.current_focus_state)
              ? d.current_focus_state
              : null;
        return {
          ...d,
          target_states: states,
          current_focus_state: nextFocus,
        };
      });
      track("onboarding.step_completed", { step: "target_states" });
      // ROADS-505: when exactly 1 state is selected, the focus-state step
      // is moot (no choice to make). Skip past it on the forward pass.
      // stepBack mirrors this — see the stepBack callback above.
      // We still emit step_completed for current_focus_state so analytics
      // observers see the same step sequence regardless of skip behavior.
      if (states.length === 1) {
        track("onboarding.step_completed", { step: "current_focus_state" });
        setStepIndex((i) => Math.min(STEP_ORDER.length - 1, i + 2));
      } else {
        stepForward();
      }
    },
    [stepForward],
  );

  const handleFocusStateNext = useCallback(
    (code: UsStateCode) => {
      setDraft((d) => ({ ...d, current_focus_state: code }));
      track("onboarding.step_completed", { step: "current_focus_state" });
      stepForward();
    },
    [stepForward],
  );

  const handleCdlNext = useCallback(
    (cdlInterest: boolean) => {
      setDraft((d) => ({ ...d, cdl_interest: cdlInterest }));
      track("onboarding.step_completed", { step: "cdl_interest" });
      stepForward();
    },
    [stepForward],
  );

  const handleSubmit = useCallback(async () => {
    if (
      draft.home_country === null ||
      draft.native_language === null ||
      draft.target_states.length === 0 ||
      draft.current_focus_state === null
    ) {
      setSubmit({
        kind: "error",
        message: t("onboarding.submitError", { message: "missing_fields" }),
        fieldErrors: [],
      });
      return;
    }
    setSubmit({ kind: "saving" });
    const update: UserProfileUpdate = {
      home_country: draft.home_country,
      native_language: draft.native_language,
      target_states: draft.target_states,
      current_focus_state: draft.current_focus_state,
      cdl_interest: draft.cdl_interest,
    };
    try {
      await putProfile(update);
      // Analytics: terminal event. Fires exactly once per user on the
      // first successful submission. Note the PII discipline: we send
      // the COUNT of target states (structural facet) but never the
      // codes, and we do NOT send `home_country` — §7 hard rule.
      track("onboarding.completed", {
        native_language: draft.native_language,
        target_state_count: draft.target_states.length,
        cdl_interest: draft.cdl_interest,
      });
      // Preserve an attempted destination passed through router state from
      // the OnboardingGate.
      const returnTo =
        (location.state as { returnTo?: string } | null)?.returnTo ?? "/";
      navigate(returnTo, { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      // Wave 14e/3 (ROADS-339): pull the per-field validation array out
      // of the new `{error:"validation_failed", fields:[…]}` body so the
      // ReviewStep can show the message inline next to the offending
      // input instead of just a single banner.
      const fieldErrors =
        err instanceof ApiError ? extractProfileFieldErrors(err.body) : [];
      setSubmit({ kind: "error", message, fieldErrors });
    }
  }, [draft, location.state, navigate, t]);

  if (load.kind === "loading") {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="onboarding-wizard-loading"
        className="mx-auto mt-xl max-w-md p-md text-center text-neutral-700"
      >
        {t("onboarding.loading")}
      </div>
    );
  }

  if (load.kind === "error") {
    // Pick the user-friendly i18n key based on the failure variant so
    // we don't surface raw fetch / kotlinx-serialization strings to the
    // learner. The verbose `loadError` key is kept around as a fallback
    // for callers that pass an arbitrary message, but the wizard always
    // shows one of the two new keys now.
    const errorKey =
      load.variant === "server"
        ? "onboarding.error.server"
        : "onboarding.error.network";
    return (
      <div
        role="alert"
        data-testid="onboarding-wizard-load-error"
        data-error-variant={load.variant}
        className="mx-auto mt-xl max-w-md rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
      >
        {t(errorKey)}
      </div>
    );
  }

  const currentKind = STEP_ORDER[stepIndex];

  return (
    <section className="space-y-lg" data-testid="onboarding-wizard">
      <StepIndicator current={stepIndex + 1} total={STEP_ORDER.length} />

      {currentKind === "home_country" && (
        <HomeCountryStep
          value={draft.home_country}
          onNext={handleHomeCountryNext}
          onBack={stepBack}
        />
      )}
      {currentKind === "native_language" && (
        <NativeLanguageStep
          value={draft.native_language}
          onNext={handleNativeLanguageNext}
        />
      )}
      {currentKind === "target_states" && (
        <TargetStatesStep
          value={draft.target_states}
          onNext={handleTargetStatesNext}
          onBack={stepBack}
        />
      )}
      {currentKind === "current_focus_state" && (
        <CurrentFocusStateStep
          targetStates={draft.target_states}
          value={draft.current_focus_state}
          onNext={handleFocusStateNext}
          onBack={stepBack}
        />
      )}
      {currentKind === "cdl_interest" && (
        <CdlInterestStep
          value={draft.cdl_interest}
          onNext={handleCdlNext}
          onBack={stepBack}
        />
      )}
      {currentKind === "review" && (
        <ReviewStep
          draft={draft}
          onBack={stepBack}
          onSubmit={handleSubmit}
          submit={submit}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Inline review step — intentionally not exported. Leaves the 5 stepfiles
// focused on user-input; this just summarises the draft and submits.
// ---------------------------------------------------------------------------
interface ReviewStepProps {
  draft: DraftProfile;
  onBack: () => void;
  onSubmit: () => void;
  submit: SubmitState;
}

function ReviewStep({ draft, onBack, onSubmit, submit }: ReviewStepProps) {
  const { t } = useTranslation();
  const country =
    draft.home_country !== null
      ? (getCountry(draft.home_country) ?? null)
      : null;
  const language =
    draft.native_language !== null
      ? (getLanguage(draft.native_language) ?? null)
      : null;
  // Wave 14e/3: index per-field errors by field name so ReviewStep can
  // place the message under the correct <dt>/<dd>. We only do this work
  // when the submit actually errored so the happy path stays cheap.
  const fieldErrorByField: Record<string, string> =
    submit.kind === "error"
      ? Object.fromEntries(
          submit.fieldErrors.map(
            (fe) => [fe.field, fe.message] as const,
          ),
        )
      : {};
  function fieldError(field: string): string | undefined {
    return fieldErrorByField[field];
  }
  return (
    <div className="space-y-md">
      <header>
        <h2 className="font-display text-xl font-semibold text-brand-primary">
          {t("onboarding.review.title")}
        </h2>
        <p className="mt-xs text-sm text-neutral-700">
          {t("onboarding.review.body")}
        </p>
      </header>

      <dl
        className="grid grid-cols-1 gap-sm rounded-md border border-brand-primary/10 bg-white p-md text-sm sm:grid-cols-2"
        data-testid="onboarding-review-summary"
      >
        <div>
          <dt className="text-xs font-medium text-neutral-600">
            {t("onboarding.review.homeCountryLabel")}
          </dt>
          <dd className="font-semibold text-brand-primary">
            {country !== null
              ? `${country.name} (${country.code})`
              : draft.home_country ?? "—"}
          </dd>
          {fieldError("home_country") && (
            <p
              data-testid="onboarding-field-error-home_country"
              className="mt-xs text-xs font-medium text-danger"
            >
              {fieldError("home_country")}
            </p>
          )}
        </div>
        <div>
          <dt className="text-xs font-medium text-neutral-600">
            {t("onboarding.review.nativeLanguageLabel")}
          </dt>
          <dd className="font-semibold text-brand-primary">
            {language !== null
              ? formatLanguageLabel(language)
              : draft.native_language ?? "—"}
          </dd>
          {fieldError("native_language") && (
            <p
              data-testid="onboarding-field-error-native_language"
              className="mt-xs text-xs font-medium text-danger"
            >
              {fieldError("native_language")}
            </p>
          )}
        </div>
        <div>
          <dt className="text-xs font-medium text-neutral-600">
            {t("onboarding.review.targetStatesLabel")}
          </dt>
          <dd className="font-semibold text-brand-primary">
            {draft.target_states.length > 0
              ? draft.target_states.join(", ")
              : "—"}
          </dd>
          {fieldError("target_states") && (
            <p
              data-testid="onboarding-field-error-target_states"
              className="mt-xs text-xs font-medium text-danger"
            >
              {fieldError("target_states")}
            </p>
          )}
        </div>
        <div>
          <dt className="text-xs font-medium text-neutral-600">
            {t("onboarding.review.currentFocusStateLabel")}
          </dt>
          <dd className="font-semibold text-brand-primary">
            {draft.current_focus_state ?? "—"}
          </dd>
          {fieldError("current_focus_state") && (
            <p
              data-testid="onboarding-field-error-current_focus_state"
              className="mt-xs text-xs font-medium text-danger"
            >
              {fieldError("current_focus_state")}
            </p>
          )}
        </div>
        <div>
          <dt className="text-xs font-medium text-neutral-600">
            {t("onboarding.review.cdlInterestLabel")}
          </dt>
          <dd className="font-semibold text-brand-primary">
            {draft.cdl_interest
              ? t("onboarding.review.cdlYes")
              : t("onboarding.review.cdlNo")}
          </dd>
        </div>
      </dl>

      {submit.kind === "error" && (
        <p
          role="alert"
          data-testid="onboarding-submit-error"
          className="rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
        >
          {t("onboarding.submitError", { message: submit.message })}
        </p>
      )}

      <div className="flex items-center justify-between gap-sm pt-md">
        <button
          type="button"
          data-testid="onboarding-back"
          onClick={onBack}
          className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-md py-sm text-sm font-medium text-brand-primary transition-colors hover:border-brand-primary/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("onboarding.back")}
        </button>
        <button
          type="button"
          data-testid="onboarding-submit"
          disabled={submit.kind === "saving"}
          onClick={onSubmit}
          className="inline-flex items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submit.kind === "saving"
            ? t("onboarding.submitting")
            : t("onboarding.submit")}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ROADS-433: marketing funnel-quiz prefill helpers.
//
// `applyOnboardingPrefill` merges the URL prefill into the existing draft.
// Stored profile fields ALWAYS win — we never overwrite a server-side answer
// with a marketing query string, because the visitor may have already
// answered the wizard once before and clicked through marketing again.
//
// `computePrefillSkipIndex` returns the lowest STEP_ORDER index whose
// answer is not satisfied by either the profile or the prefill. Steps
// before that index can be skipped over.
//
// hasLicense + adult are not currently surfaced in the wizard's step list;
// they're collected by the marketing quiz for telemetry + result-page
// branching but the SPA wizard doesn't have a dedicated step for them at
// MVP. They're intentionally dropped here — promoting them to their own
// wizard steps is a follow-up ticket.
// ---------------------------------------------------------------------------
export function applyOnboardingPrefill(
  draft: DraftProfile,
  prefill: OnboardingPrefill,
): DraftProfile {
  const merged: DraftProfile = { ...draft };
  if (merged.home_country === null && prefill.country !== null) {
    merged.home_country = prefill.country;
  }
  if (prefill.state !== null) {
    if (merged.target_states.length === 0) {
      merged.target_states = [prefill.state];
    }
    if (merged.current_focus_state === null) {
      merged.current_focus_state = prefill.state;
    }
  }
  return merged;
}

export function computePrefillSkipIndex(
  draft: DraftProfile,
  prefill: OnboardingPrefill,
): number {
  // Iterate STEP_ORDER and stop at the first step whose answer is missing.
  // We treat every step except `review` here. The result is the index of
  // the first un-answered step — that becomes the wizard's start index.
  for (let i = 0; i < STEP_ORDER.length; i += 1) {
    const step = STEP_ORDER[i];
    switch (step) {
      case "home_country":
        if (draft.home_country === null && prefill.country === null) return i;
        break;
      case "native_language":
        // The marketing quiz does not collect native language. If the
        // profile didn't carry one, this is the step to land on.
        if (draft.native_language === null) return i;
        break;
      case "target_states":
        if (draft.target_states.length === 0 && prefill.state === null) {
          return i;
        }
        break;
      case "current_focus_state":
        if (draft.current_focus_state === null && prefill.state === null) {
          return i;
        }
        break;
      case "cdl_interest":
        // CDL interest is never carried by the funnel quiz. Always require
        // an explicit click-through here.
        return i;
      case "review":
        return i;
    }
  }
  return STEP_ORDER.indexOf("review");
}
